import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Mountain, Infinity as InfinityIcon, ArrowUpRight } from "lucide-react";

const LEGACY_IMG =
  "https://images.unsplash.com/photo-1529518152792-d08317b26e22?crop=entropy&cs=srgb&fm=jpg&ixid=M3w7NDk1Nzh8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGZhbWlseSUyMHdhbGtpbmclMjBuYXR1cmV8ZW58MHx8fHwxNzgyODU2MzIxfDA&ixlib=rb-4.1.0&q=85";

const ARCH_IMG =
  "https://images.unsplash.com/photo-1489058535093-8f530d789c3b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBidWlsZGluZyUyMHN1bnNldHxlbnwwfHx8fDE3ODI4NTYzMjF8MA&ixlib=rb-4.1.0&q=85";

const PILLARS = [
  {
    letter: "Ra",
    name: "René",
    meaning: "Renacimiento",
    icon: Sparkles,
    full:
      "René, en su raíz latina, significa renacido. Es el verbo del comienzo. En RAZMEK lo asumimos como la actitud de cada conversación: empezar siempre con una mirada nueva, sin sesgos, sin estructuras heredadas que no sirven al cliente.",
    extra:
      "Cada estrategia patrimonial nace con un diagnóstico desnudo. Reconstruimos desde cero la arquitectura financiera de cada familia o empresa.",
  },
  {
    letter: "Z",
    name: "Azpeitia",
    meaning: "Roca, estabilidad",
    icon: Mountain,
    full:
      "Azpeitia proviene del euskera: aitz pean, “bajo la peña”. Es el lugar bajo la roca. Símbolo de aquello inamovible, de aquello que sostiene.",
    extra:
      "La estabilidad es el segundo principio. Solo recomendamos instrumentos respaldados por instituciones con calificación crediticia sólida y trayectoria comprobada. La estructura antes que el ruido.",
  },
  {
    letter: "Mek",
    name: "Smeke",
    meaning: "Legado y continuidad",
    icon: InfinityIcon,
    full:
      "Smeke, en su raíz hebrea, evoca la continuidad y la transmisión. Lo que se construye no es para uno: es para los que siguen.",
    extra:
      "El horizonte de cualquier asesoría es generacional. Las pólizas, los portafolios y las estructuras fiduciarias que diseñamos están pensadas para trascender al titular.",
  },
];

const PillarBlock = ({ pillar }) => {
  const Icon = pillar.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.7 }}
      className="grid grid-cols-1 lg:grid-cols-12 gap-12"
    >
      <div className="lg:col-span-4">
        <span
          className="block font-serif text-[12rem] leading-none text-razmek-gold/20"
          style={{ marginTop: "-1rem" }}
        >
          {pillar.letter}
        </span>
        <div className="flex items-center gap-4 mt-4">
          <Icon size={28} className="text-razmek-gold" strokeWidth={1.3} />
          <div className="eyebrow text-razmek-gray">{pillar.meaning}</div>
        </div>
      </div>
      <div className="lg:col-span-8">
        <h2 className="font-serif text-5xl lg:text-6xl text-razmek-dark leading-tight">
          {pillar.name}
        </h2>
        <div className="mt-8 space-y-6 font-sans text-base lg:text-lg leading-relaxed text-razmek-dark/75 max-w-xl">
          <p>{pillar.full}</p>
          <p className="border-l-2 border-razmek-gold pl-6 italic text-razmek-blue">
            {pillar.extra}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

const PillarsSection = () => (
  <section className="py-24 lg:py-32">
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 space-y-32">
      {PILLARS.map((p) => (
        <PillarBlock key={p.name} pillar={p} />
      ))}
    </div>
  </section>
);

const Historia = () => {
  return (
    <main className="bg-white">
      {/* Intro */}
      <section className="pt-32 lg:pt-40 pb-20 lg:pb-28 bg-razmek-alt">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <div className="eyebrow">Nuestra historia</div>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl mt-6 leading-[1.02] text-razmek-dark max-w-4xl">
            Un nombre que <span className="italic text-razmek-blue">renace</span>,
            sostiene y <span className="italic text-razmek-blue">trasciende</span>
            <span className="text-razmek-gold">.</span>
          </h1>
          <p className="mt-10 font-sans text-lg lg:text-xl leading-relaxed text-razmek-dark/70 max-w-3xl">
            RAZMEK no es una palabra inventada. Es la fusión de tres apellidos y
            de tres formas de entender el patrimonio. Cada letra es un
            principio. Cada principio, una manera de acompañar a nuestros
            clientes.
          </p>
        </div>
      </section>

      {/* Architecture image */}
      <section className="relative h-[60vh] overflow-hidden">
        <img src={ARCH_IMG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-razmek-dark/30" />
        <div className="absolute inset-0 flex items-end p-10 lg:p-16">
          <p className="font-serif italic text-white text-2xl lg:text-3xl max-w-xl leading-snug">
            “El patrimonio se construye como se construye la arquitectura: con
            cimientos invisibles que sostienen lo que la vista admira.”
          </p>
        </div>
      </section>

      {/* Pillars expanded */}
      <PillarsSection />

      {/* Legacy section */}
      <section className="bg-razmek-blue text-white py-24 lg:py-32 relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="eyebrow text-razmek-gold">Síntesis</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mt-6 leading-[1.05]">
              Proteger, sostener,
              <br />
              <span className="italic">trascender</span>.
            </h2>
            <p className="mt-8 font-sans text-lg leading-relaxed text-white/70 max-w-md">
              Esa es la promesa que nos pone de pie cada mañana. Lo decimos en
              tres palabras porque tres es lo que somos: tres apellidos, tres
              principios, tres generaciones acompañadas.
            </p>
            <Link
              to="/precalificacion"
              className="group mt-12 inline-flex items-center gap-3 bg-razmek-gold hover:bg-white hover:text-razmek-dark text-white px-7 py-4 font-sans tracking-[0.16em] uppercase text-[11px] transition-all"
            >
              Comenzar mi historia con razmek
              <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="relative h-[500px]">
            <img
              src={LEGACY_IMG}
              alt="Familia caminando en la naturaleza"
              className="w-full h-full object-cover"
            />
            <div className="absolute -bottom-4 -left-4 bg-razmek-gold w-32 h-32 -z-0 hidden lg:block" />
          </div>
        </div>
      </section>
    </main>
  );
};

export default Historia;
